import { useEffect, useState } from "react";
import { api, type Project, type Note } from "../lib/api";
import { NoteCard } from "../components/NoteCard";
import { Plus, Loader2, Folder, Trash2, ArrowLeft } from "lucide-react";

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Project | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getProjects();
      setProjects(res.projects);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function openProject(p: Project) {
    setSelected(p);
    try {
      const res = await api.getProjectNotes(p.id);
      setNotes(res.notes);
    } catch (e) {
      console.error(e);
    }
  }

  async function create() {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      await api.createProject({ name: newName.trim() });
      setNewName("");
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Удалить проект?")) return;
    await api.deleteProject(id);
    if (selected?.id === id) setSelected(null);
    await load();
  }

  if (selected) {
    return (
      <div className="px-4 pt-4 pb-28">
        <button
          onClick={() => setSelected(null)}
          className="flex items-center gap-2 text-white/60 text-sm mb-4"
        >
          <ArrowLeft size={16} /> Назад
        </button>
        <h1 className="text-xl font-bold mb-4 flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ background: selected.color }}
          />
          {selected.name}
        </h1>
        {notes.length === 0 ? (
          <p className="text-white/40 text-sm">В этом проекте пока нет заметок</p>
        ) : (
          <div className="space-y-3">
            {notes.map((n) => (
              <NoteCard key={n.id} note={n} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-28">
      <h1 className="text-xl font-bold mb-4">Проекты</h1>

      <div className="flex gap-2 mb-6">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="Новый проект..."
          className="flex-1 bg-surface-card border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent/50"
        />
        <button
          onClick={create}
          disabled={creating || !newName.trim()}
          className="bg-accent disabled:opacity-40 rounded-xl px-4 flex items-center"
        >
          {creating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-accent" size={24} />
        </div>
      )}

      {!loading && projects.length === 0 && (
        <p className="text-white/40 text-sm text-center py-8">
          Создай первый проект, чтобы группировать заметки
        </p>
      )}

      <div className="space-y-2">
        {projects.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-3 bg-surface-card border border-white/5 rounded-xl p-3"
          >
            <button
              onClick={() => openProject(p)}
              className="flex-1 flex items-center gap-3 text-left"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: p.color + "33" }}
              >
                <Folder size={18} style={{ color: p.color }} />
              </div>
              <span className="font-medium text-sm">{p.name}</span>
            </button>
            <button
              onClick={() => remove(p.id)}
              className="p-2 text-white/30 hover:text-red-400"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}